import { authFetch } from '../../../utils/authFetch';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { TemplateSelector } from '../../../components/TemplateSelector';
import { TemplatePreview } from '../../../components/TemplatePreview';
import { CapabilityTemplate } from '../../../types/api';

interface Member {
  id: string;
  userId: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  status: 'active' | 'pending';
  type: 'member' | 'invite';
  inviteId?: string;
  expiresAt?: string;
  templateId?: string | null;
  templateName?: string | null;
}

interface SeatsInfo {
  current: number;
  included: number;
  additional: number;
  perSeatCents: number;
}

interface MembersTabProps {
  workspaceId: string;
  authToken: string;
  workspaceType: 'personal' | 'team' | 'enterprise';
}

const roleLabels: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

const roleColors: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  member: 'bg-gray-100 text-gray-700',
  viewer: 'bg-gray-100 text-gray-500',
};

/**
 * Members settings tab
 *
 * Features:
 * - Member list with roles
 * - Pending invites
 * - Invite new members
 * - Remove/resend/cancel invites
 * - Seat count and pricing summary
 */
export function MembersTab({ workspaceId, workspaceType, authToken }: MembersTabProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [seats, setSeats] = useState<SeatsInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [inviteTemplate, setInviteTemplate] = useState<CapabilityTemplate | null>(null);
  const [inviteStep, setInviteStep] = useState<'details' | 'permissions'>('details');
  const [isInviting, setIsInviting] = useState(false);
  const [actionMemberId, setActionMemberId] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<Member | null>(null);

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  async function loadData() {
    try {
      const [peopleRes, pricingRes] = await Promise.all([
        authFetch(`/api/workspaces/${workspaceId}/people`, authToken),
        authFetch(`/api/workspaces/${workspaceId}/pricing`, authToken),
      ]);

      if (peopleRes.ok) {
        const data = await peopleRes.json();
        setMembers(data.people || []);
      }

      if (pricingRes.ok) {
        const data = await pricingRes.json();
        setSeats(data.pricing?.seats || null);
      }
    } catch {
      toast.error('Failed to load members');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleInvite() {
    if (!inviteEmail.trim()) return;

    setIsInviting(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/members`, authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          email: inviteEmail.trim(),
          role: inviteRole,
          templateId: inviteTemplate?.id || null,
        }),
      });

      if (response.ok) {
        toast.success('Invitation sent');
        closeInviteModal();
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to send invitation');
      }
    } catch {
      toast.error('Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  }

  function closeInviteModal() {
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteRole('member');
    setInviteTemplate(null);
    setInviteStep('details');
  }

  async function handleUpdateMemberTemplate(memberId: string, templateId: string | null) {
    setActionMemberId(memberId);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/members/${memberId}`, authToken, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ templateId }),
      });

      if (response.ok) {
        toast.success('Permissions updated');
        setEditingMember(null);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update permissions');
      }
    } catch {
      toast.error('Failed to update permissions');
    } finally {
      setActionMemberId(null);
    }
  }

  async function handleRemoveMember(memberId: string) {
    setActionMemberId(memberId);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/members/${memberId}`, authToken, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        toast.success('Member removed');
        setMembers((prev) => prev.filter((m) => m.id !== memberId));
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to remove member');
      }
    } catch {
      toast.error('Failed to remove member');
    } finally {
      setActionMemberId(null);
    }
  }

  async function handleResendInvite(inviteId: string) {
    setActionMemberId(inviteId);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/invites/${inviteId}/resend`, authToken, {
        method: 'POST',
        
      });

      if (response.ok) {
        toast.success('Invitation resent');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to resend invitation');
      }
    } catch {
      toast.error('Failed to resend invitation');
    } finally {
      setActionMemberId(null);
    }
  }

  async function handleCancelInvite(inviteId: string) {
    setActionMemberId(inviteId);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/invites/${inviteId}`, authToken, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        toast.success('Invitation cancelled');
        setMembers((prev) => prev.filter((m) => m.inviteId !== inviteId));
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to cancel invitation');
      }
    } catch {
      toast.error('Failed to cancel invitation');
    } finally {
      setActionMemberId(null);
    }
  }

  async function handleChangeRole(memberId: string, newRole: string) {
    setActionMemberId(memberId);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/members/${memberId}`, authToken, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ role: newRole }),
      });

      if (response.ok) {
        toast.success('Role updated');
        setMembers((prev) =>
          prev.map((m) =>
            m.id === memberId ? { ...m, role: newRole as Member['role'] } : m
          )
        );
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update role');
      }
    } catch {
      toast.error('Failed to update role');
    } finally {
      setActionMemberId(null);
    }
  }

  const activeMembers = members.filter((m) => m.type === 'member');
  const pendingInvites = members.filter((m) => m.type === 'invite');

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Seat Summary */}
      {seats && workspaceType !== 'personal' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Team Seats</h3>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {seats.current} <span className="text-base font-normal text-gray-500">/ {seats.included} included</span>
              </p>
            </div>
            {seats.additional > 0 && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Additional seats</p>
                <p className="text-lg font-medium text-gray-900">
                  {seats.additional} x ${(seats.perSeatCents / 100).toFixed(2)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Members List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            Team Members ({activeMembers.length})
          </h2>
          {workspaceType !== 'personal' && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              Invite Member
            </button>
          )}
        </div>
        <div className="divide-y divide-gray-100">
          {activeMembers.map((member) => (
            <div key={member.id} className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                  {member.avatarUrl ? (
                    <img
                      src={member.avatarUrl}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-medium text-gray-500">
                      {member.name?.charAt(0).toUpperCase() || member.email?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{member.name || 'Unknown'}</p>
                  <p className="text-sm text-gray-500">{member.email}</p>
                  {member.templateName && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Permissions: {member.templateName}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {member.role === 'owner' ? (
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${roleColors[member.role]}`}>
                    {roleLabels[member.role]}
                  </span>
                ) : (
                  <>
                    <select
                      value={member.role}
                      onChange={(e) => handleChangeRole(member.id, e.target.value)}
                      disabled={actionMemberId === member.id}
                      className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    <button
                      onClick={() => setEditingMember(member)}
                      disabled={actionMemberId === member.id}
                      className="p-2 text-gray-400 hover:text-blue-600 disabled:opacity-50"
                      title="Edit permissions"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </button>
                  </>
                )}
                {member.role !== 'owner' && (
                  <button
                    onClick={() => handleRemoveMember(member.id)}
                    disabled={actionMemberId === member.id}
                    className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-50"
                    title="Remove member"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
          {activeMembers.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-500">
              No team members yet
            </div>
          )}
        </div>
      </div>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Pending Invites ({pendingInvites.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {pendingInvites.map((invite) => (
              <div key={invite.inviteId || invite.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{invite.email}</p>
                    <p className="text-sm text-gray-500">
                      Invited as {roleLabels[invite.role]}
                      {invite.templateName && <> &middot; {invite.templateName}</>}
                      {invite.expiresAt && (
                        <> &middot; Expires {new Date(invite.expiresAt).toLocaleDateString()}</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleResendInvite(invite.inviteId!)}
                    disabled={actionMemberId === invite.inviteId}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    Resend
                  </button>
                  <button
                    onClick={() => handleCancelInvite(invite.inviteId!)}
                    disabled={actionMemberId === invite.inviteId}
                    className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {inviteStep === 'details' ? 'Invite Team Member' : 'Set Permissions'}
              </h3>
              <button onClick={closeInviteModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {inviteStep === 'details' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="teammate@company.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as typeof inviteRole)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="admin">Admin - Can manage members and billing</option>
                      <option value="member">Member - Full access to workspace</option>
                      <option value="viewer">Viewer - Read-only access</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-4">
                      Choose a permission template for <span className="font-medium">{inviteEmail}</span>
                    </p>
                    <TemplateSelector
                      workspaceId={workspaceId}
                      selectedTemplateId={inviteTemplate?.id || null}
                      onSelect={setInviteTemplate}
                      suggestedRole={inviteRole}
                    />
                  </div>
                  {inviteTemplate && (
                    <div className="pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Permission Preview</h4>
                      <TemplatePreview
                        workspaceId={workspaceId}
                        templateId={inviteTemplate.id}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              {inviteStep === 'permissions' ? (
                <>
                  <button
                    onClick={() => setInviteStep('details')}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleInvite}
                    disabled={isInviting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isInviting ? 'Sending...' : 'Send Invite'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={closeInviteModal}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setInviteStep('permissions')}
                    disabled={!inviteEmail.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    Next: Set Permissions
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Permissions Modal */}
      {editingMember && (
        <EditMemberPermissionsModal
          workspaceId={workspaceId}
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSave={(templateId) => handleUpdateMemberTemplate(editingMember.id, templateId)}
          isLoading={actionMemberId === editingMember.id}
        />
      )}
    </div>
  );
}

/**
 * Modal for editing a member's permission template
 */
interface EditMemberPermissionsModalProps {
  workspaceId: string;
  member: Member;
  onClose: () => void;
  onSave: (templateId: string | null) => void;
  isLoading: boolean;
}

function EditMemberPermissionsModal({
  workspaceId,
  member,
  onClose,
  onSave,
  isLoading,
}: EditMemberPermissionsModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<CapabilityTemplate | null>(null);
  const [initialTemplateId] = useState(member.templateId);

  // Track if selection has changed
  const hasChanges = selectedTemplate?.id !== initialTemplateId;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Edit Permissions</h3>
            <p className="text-sm text-gray-500">{member.name || member.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Select a permission template for this member
              </p>
              <TemplateSelector
                workspaceId={workspaceId}
                selectedTemplateId={selectedTemplate?.id || member.templateId || null}
                onSelect={setSelectedTemplate}
                suggestedRole={member.role === 'owner' ? undefined : member.role}
              />
            </div>
            {selectedTemplate && (
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Permission Preview</h4>
                <TemplatePreview
                  workspaceId={workspaceId}
                  templateId={selectedTemplate.id}
                />
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(selectedTemplate?.id || null)}
            disabled={isLoading || !hasChanges}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
